import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef, useMemo } from "react";
import { useLatestCallback } from "@/hooks/useLatestCallback";
import { fixedSlots } from "@/lib/listIdentity";
import { ShowChat } from "@/components/ShowChat";
import { TalentCardPicker as CardPicker } from "@/components/TalentCardPicker";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RarityPill } from "@/components/Atoms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Drama, Eye, Clock, Radio, ThumbsUp, ThumbsDown, Sparkles,
  Citrus, Send, Trophy, Coins, Users, LogOut, X, Ticket, Flame, Megaphone, Crown, Lock,
} from "lucide-react";

const RARITY_COLORS = {
  common: "#9ca3af", uncommon: "#34d399", rare: "#22d3ee",
  epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6",
};
const STAT_LABELS = [["atk", "ATK", "#fb7185"], ["def", "DEF", "#38bdf8"], ["hp", "HP", "#34d399"], ["spd", "SPD", "#fbbf24"]];
const PRAISE_AMOUNTS = [5, 25, 100];
const SHAME_AMOUNTS = [5, 15, 60];

function fmtTime(iso) { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
function centerOut(items) {
  const out = [];
  items.forEach((it, i) => (i % 2 === 0 ? out.push(it) : out.unshift(it)));
  return out;
}

/* ── Live countdown to a future ISO timestamp ──────────────────────────── */
function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return { ms: 0, started: true, label: "00:00" };
  const ms = Math.max(0, new Date(iso).getTime() - now);
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const label = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  return { ms, started: ms <= 0, label };
}

/* ── A card face used on stage and in the stands ───────────────────────── */
function CardFace({ card, size = "stage", fx, fit }) {
  const color = RARITY_COLORS[card?.rarity] || "#9ca3af";
  const dims = size === "stage" ? "w-48 sm:w-56" : size === "seat" ? "w-24 sm:w-28" : "w-16";
  const ratio = card?.orientation === "landscape" ? "aspect-[4/3]" : "aspect-[3/4]";
  // In the stands / line-up we want the ENTIRE card visible (no crop) so use contain.
  const objectFit = fit || (size === "seat" ? "contain" : (card?.image_fit || "cover"));
  return (
    <div className={`${dims} ${ratio} rounded-xl overflow-hidden relative border-2 bg-black/60`} style={{ borderColor: color, boxShadow: `0 0 24px ${color}55` }}>
      {card?.front_image ? (
        <img src={card.front_image} alt={card.title}
             className="absolute inset-0 w-full h-full"
             style={{ objectFit, objectPosition: objectFit === "contain" ? "center" : `${card?.image_pos_x ?? 50}% ${card?.image_pos_y ?? 50}%`, transform: objectFit === "contain" ? "none" : `scale(${card?.image_scale || 1})` }} />
      ) : (
        <div className="absolute inset-0 grid place-items-center bg-black/60 text-white/40 text-xs">No image</div>
      )}
      {/* shame splat overlay */}
      {fx?.kind === "shame" && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          {fixedSlots(1 + (fx.intensity || 1)).map(({ id, index: i }) => (
            <motion.div key={`${fx.id}:${id}`} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1.2, opacity: 0.9 }}
              className="absolute text-3xl" style={{ left: `${20 + i * 18}%`, top: `${25 + (i % 2) * 30}%` }}>🍅</motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Full-screen overlay, opened only by an explicit click/tap ────────── */
function StageFullScreen({ card, onClose }) {
  useEffect(() => {
    const closeOnEscape = event => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  const color = RARITY_COLORS[card?.rarity] || "#9ca3af";
  const ratio = card?.orientation === "landscape" ? "aspect-[4/3]" : "aspect-[3/4]";
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md grid place-items-center p-6"
      data-testid="ts-stage-fullscreen"
    >
      <motion.div
        initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
        transition={{ type: "spring", stiffness: 200, damping: 24 }}
        className={`${ratio} h-[85vh] max-w-[92vw] rounded-2xl overflow-hidden relative border-4`}
        style={{ borderColor: color, boxShadow: `0 0 80px ${color}` }}
      >
        {card?.front_image
          ? <img src={card.front_image} alt={card.title} className="absolute inset-0 w-full h-full object-contain bg-black" />
          : <div className="absolute inset-0 grid place-items-center bg-black/70 text-white/40">No image</div>}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-5">
          <div className="font-display text-3xl text-white tracking-wide">{card.title}</div>
          <div className="flex items-center gap-2 mt-1"><RarityPill rarity={card.rarity} /><span className="text-white/60 text-sm">by {card.owner_name}{card.edition_number ? ` · #${card.edition_number}` : ""}</span></div>
        </div>
      </motion.div>
      <button onClick={onClose} aria-label="Close card preview" data-testid="ts-stage-fullscreen-close" className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"><X className="h-5 w-5" /></button>
    </motion.div>
  );
}

/* ── Floating crowd reactions (like/dislike/praise/shame) that splat on the
   stage card and always name the judge who sent them ───────────────────── */
const REACTION_CONF = {
  like: { color: "#34d399", verb: "liked" },
  dislike: { color: "#fb7185", verb: "disliked" },
  praise: { color: "#fbbf24", verb: "praised" },
  shame: { color: "#a855f7", verb: "shamed" },
};
function ReactionIcon({ kind, color }) {
  if (kind === "like") return <ThumbsUp className="h-9 w-9" style={{ color }} fill={color} />;
  if (kind === "dislike") return <ThumbsDown className="h-9 w-9" style={{ color }} fill={color} />;
  if (kind === "praise") return <span className="text-4xl leading-none">🌟</span>;
  return <span className="text-4xl leading-none">🍅</span>;
}
function ReactionsOverlay({ reactions }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible z-30" data-testid="ts-reactions">
      <AnimatePresence>
        {(reactions || []).map((r) => {
          const conf = REACTION_CONF[r.kind] || REACTION_CONF.like;
          const startX = ((r.id * 53) % 160) - 80; // deterministic launch offset −80..80
          return (
            <motion.div
              key={r.id}
              initial={{ x: startX, y: 170, scale: 0.3, opacity: 0, rotate: -18 }}
              animate={{ x: [startX, startX * 0.4, 0], y: [170, 30, -6], scale: [0.3, 1.55, 1.1], opacity: [0, 1, 1, 0], rotate: [-18, 10, 0] }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 1.5, times: [0, 0.5, 0.8, 1], ease: "easeOut" }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
              data-testid={`ts-reaction-${r.kind}`}
            >
              <div className="grid place-items-center" style={{ filter: `drop-shadow(0 0 12px ${conf.color})` }}>
                <ReactionIcon kind={r.kind} color={conf.color} />
              </div>
              <span className="mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap backdrop-blur-sm"
                    style={{ background: `${conf.color}26`, color: conf.color, border: `1px solid ${conf.color}77` }}>
                {r.from} {conf.verb}
              </span>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

/* ── Stage with praise(spin+glow)/shame(darken+fade) animations ────────── */
const STAT_COLOR = { atk: "#fb7185", def: "#38bdf8", hp: "#34d399", spd: "#fbbf24" };

/* ── Talent Show show-off: chat-bubble over the stage card with the flaunted
   stat/trait. Fires only from a `showoff` event. ─────────────────────────── */
function ShowoffBubbles({ showoffs }) {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 -top-3 flex flex-col-reverse items-center gap-1.5 pointer-events-none z-40" data-testid="ts-showoff-bubbles">
      <AnimatePresence>
        {(showoffs || []).map((s) => {
          const color = STAT_COLOR[s.attr] || "#c084fc";
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24, scale: 0.6 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.8 }}
              transition={{ type: "spring", stiffness: 320, damping: 18 }}
              className="relative"
              data-testid="ts-showoff-bubble"
            >
              <div
                className="px-3 py-1.5 rounded-2xl font-display text-sm text-black shadow-lg whitespace-nowrap flex items-center gap-1.5"
                style={{ background: color, boxShadow: `0 8px 24px ${color}99` }}
              >
                <Megaphone className="h-3.5 w-3.5" />
                {s.display}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 rotate-45" style={{ background: color }} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

function ShowcaseStage({ current, fx, reactions, showoffs = [], shownOff = [] }) {
  const [zoom, setZoom] = useState(false);
  if (!current) return <div className="text-white/40 py-20 text-center">Curtain's about to rise…</div>;
  const flaunted = new Set(shownOff || []);
  const praise = fx?.kind === "praise";
  const shame = fx?.kind === "shame";
  const spin = praise ? 360 * (fx.intensity || 1) : 0;
  const dim = shame ? Math.max(0.25, 1 - 0.2 * (fx.intensity || 1)) : 1;
  const color = RARITY_COLORS[current.rarity] || "#9ca3af";
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
      <div className="relative">
      <motion.div
        key={current.card_id}
        initial={{ x: 240, opacity: 0, rotate: 8 }}
        animate={{
          x: 0, opacity: dim, rotate: spin,
          filter: shame ? `brightness(${dim})` : praise ? "brightness(1.6)" : "brightness(1)",
        }}
        exit={{ x: -240, opacity: 0, rotate: -8 }}
        transition={{ x: { type: "spring", stiffness: 120, damping: 18 }, rotate: { duration: praise ? 1 : 0.4 }, filter: { duration: 0.6 }, opacity: { duration: 0.6 } }}
        className="relative cursor-zoom-in"
        style={praise ? { filter: `drop-shadow(0 0 30px ${color})` } : {}}
        role="button"
        tabIndex={0}
        aria-label={`Enlarge ${current.title}`}
        onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setZoom(true); } }}
        onClick={() => setZoom(true)}
        data-testid="ts-stage-card"
      >
        <CardFace card={current} size="stage" fx={fx} />
        {praise && (
          <motion.div
            className="absolute -inset-3 rounded-2xl pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.5, 0.95, 0.5] }}
            transition={{ duration: 1, repeat: Infinity }}
            style={{ boxShadow: `0 0 60px 14px ${color}, 0 0 120px 30px ${color}55`, border: `2px solid ${color}` }}
            data-testid="ts-praise-glow"
          />
        )}
      </motion.div>
      <ReactionsOverlay reactions={reactions} />
      <ShowoffBubbles showoffs={showoffs} />
      </div>
      <div className="text-center sm:text-left">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Now performing</div>
        <div className="font-display text-3xl text-white tracking-wide mt-1">{current.title}</div>
        <div className="flex items-center gap-2 justify-center sm:justify-start mt-2">
          <RarityPill rarity={current.rarity} />
          <span className="text-white/50 text-xs">by {current.owner_name}{current.edition_number ? ` · #${current.edition_number}` : ""}</span>
        </div>
        <div className="grid grid-cols-4 gap-2 mt-4 max-w-xs">
          {STAT_LABELS.map(([k, lbl, c]) => {
            const hot = flaunted.has(k);
            return (
              <motion.div
                key={k}
                animate={hot ? { scale: [1, 1.14, 1] } : { scale: 1 }}
                transition={{ duration: 0.55 }}
                className={`hud-pill rounded-lg py-1.5 text-center relative ${hot ? "ring-2" : ""}`}
                style={hot ? { boxShadow: `0 0 18px ${c}`, borderColor: c, "--tw-ring-color": c } : {}}
                data-testid={`ts-stat-${k}${hot ? "-flaunted" : ""}`}
              >
                <div className="font-mono text-[8px] uppercase tracking-wider" style={{ color: c }}>{lbl}</div>
                <div className="font-display text-lg tabular-nums" style={{ color: hot ? c : "#fff" }}>{current[k]}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
      <AnimatePresence>{zoom && <StageFullScreen card={current} onClose={() => setZoom(false)} />}</AnimatePresence>
    </div>
  );
}

/* ── Colosseum stands (visitor cards, packed center-out) ───────────────── */
function Stands({ stands }) {
  const ordered = useMemo(() => centerOut(stands || []), [stands]);
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[hsl(260_30%_10%)] to-[hsl(230_30%_6%)] p-4" data-testid="ts-stands">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 mb-3 flex items-center gap-1"><Users className="h-3 w-3" /> The Stands · {stands?.length || 0}</div>
      <div className="flex flex-wrap justify-center gap-3">
        {ordered.length === 0 && <div className="text-white/30 text-xs py-6">Empty seats… be the first in the stands.</div>}
        {ordered.map((v, i) => (
          <motion.div key={v.user_id} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.4) }} className="flex flex-col items-center gap-1.5" data-testid={`ts-stand-seat-${v.user_id}`}>
            <CardFace card={v.card} size="seat" />
            <span className="text-[10px] text-white/65 max-w-[7rem] truncate text-center font-medium">{v.user_name}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ── Show-off (owner) + Crowd Hype (judges) ────────────────────────────── */
const SHOWOFF_STATS = [["atk", "ATK", "#fb7185"], ["def", "DEF", "#38bdf8"], ["hp", "HP", "#34d399"], ["spd", "SPD", "#fbbf24"]];
function showoffLabel(key, card) {
  if (!key) return "";
  if (key.startsWith("trait:")) return key.slice(6);
  return `${key.toUpperCase()} ${card?.[key] ?? ""}`;
}

function CrowdHype({ live, isOwner, myRole, onShowoff, onHype }) {
  const current = live?.current;
  if (!current) return null;
  const shown = live?.shown_off || [];
  const hype = live?.hype || 0;
  const bonus = live?.stage_bonus_secs || 0;
  const cap = 60;
  const traits = current.traits || [];
  const maxed = bonus >= cap;
  return (
    <div className="mt-6 rounded-2xl border border-amber-400/20 bg-amber-500/5 p-4 space-y-3" data-testid="ts-hype-panel">
      {/* crowd-hype meter */}
      <div>
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-amber-200/80 mb-1.5">
          <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5 text-amber-300" /> Crowd Hype</span>
          <span data-testid="ts-hype-count">{hype} hype{hype === 1 ? "" : "s"} · +{bonus}s{maxed ? " (max)" : ""}</span>
        </div>
        <div className="h-2.5 rounded-full bg-black/40 overflow-hidden border border-white/10">
          <motion.div className="h-full bg-gradient-to-r from-amber-400 to-rose-400"
            animate={{ width: `${Math.min(100, (bonus / cap) * 100)}%` }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }} data-testid="ts-hype-meter" />
        </div>
      </div>

      {/* what the performer is flaunting */}
      {shown.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="ts-shownoff">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 flex items-center gap-1"><Megaphone className="h-3 w-3" /> Showing off</span>
          {shown.map((k) => (
            <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-100 font-medium">{showoffLabel(k, current)}</span>
          ))}
        </div>
      )}

      {/* owner: flaunt buttons */}
      {isOwner && (
        <div data-testid="ts-showoff-controls">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-2">Flaunt a stat or trait to the crowd</div>
          <div className="flex flex-wrap gap-2">
            {SHOWOFF_STATS.map(([k, lbl, c]) => {
              const used = shown.includes(k);
              return (
                <button key={k} data-testid={`ts-showoff-${k}`} disabled={used} onClick={() => onShowoff(k)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${used ? "border-white/5 bg-black/40 text-white/25 cursor-not-allowed" : "border-white/15 text-white hover:border-amber-400/60 hover:bg-amber-400/10"}`}
                  style={used ? {} : { color: c }}>
                  {lbl} {current[k]}
                </button>
              );
            })}
            {traits.map((t) => {
              const key = `trait:${t.name}`;
              const used = shown.includes(key);
              return (
                <button key={key} data-testid={`ts-showoff-trait`} disabled={used} onClick={() => onShowoff(key)} title={t.desc}
                  className={`px-3 py-1.5 rounded-lg border text-xs transition-all ${used ? "border-white/5 bg-black/40 text-white/25 cursor-not-allowed" : "border-fuchsia-400/30 text-fuchsia-200 hover:border-fuchsia-400/60 hover:bg-fuchsia-400/10"}`}>
                  ✦ {t.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* judges: hype button */}
      {myRole === "visitor" && (
        <Button data-testid="ts-hype-btn" disabled={maxed} onClick={onHype}
          className="w-full bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-40">
          <Flame className="h-4 w-4 mr-1.5" /> {maxed ? "Crowd's maxed out" : `Hype it! (1C → +${live?.hype_bonus_secs || 5}s)`}
        </Button>
      )}
    </div>
  );
}

/* ── Results ceremony ──────────────────────────────────────────────────── */
function ResultsView({ results }) {
  if (!results || results.empty) {
    return <div className="hud-pill rounded-2xl p-10 text-center text-white/50" data-testid="ts-results-empty">This show had no performers. No awards this round.</div>;
  }
  return (
    <div data-testid="ts-results">
      <div className="text-center mb-6">
        <Trophy className="h-10 w-10 text-amber-300 mx-auto" />
        <h2 className="font-display text-3xl text-white tracking-wide mt-2">THE RESULTS</h2>
        <p className="text-white/50 text-sm">{results.entrant_count} performed · {results.visitor_count} judged · performers and judges earned 5C</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {(results.categories || []).map((cat) => (
          <div key={cat.key} className="hud-pill rounded-2xl p-4" data-testid={`ts-cat-${cat.key}`}>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-300 mb-2">{cat.label}</div>
            {["top", "bottom"].map((slot) => (
              <div key={slot} className="flex items-center gap-2 py-1">
                <span className={`text-[9px] font-mono uppercase w-10 ${slot === "top" ? "text-emerald-300" : "text-rose-300"}`}>{slot === "top" ? "Top" : "Low"}</span>
                <CardFace card={cat[slot].card} size="seat" />
                <div className="min-w-0">
                  <div className="text-white text-xs truncate">{cat[slot].card.title}</div>
                  <div className="text-white/40 text-[10px]">{cat[slot].card.owner_name} · {cat[slot].value}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TalentShowRoom() {
  const { showId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [enterOpen, setEnterOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [spectateOpen, setSpectateOpen] = useState(false);
  const [giftKind, setGiftKind] = useState(null);
  const [giftAmount, setGiftAmount] = useState(5);

  // Live state — SSE (below) pushes an instant wake-up on every stage
  // advance/vote/gift/hype/showoff; this poll is now just a slow safety net
  // (was a brutal 250ms fixed poll).
  const { data: live } = useQuery({
    queryKey: ["ts-live", showId, user?.id],
    queryFn: async () => (await api.get(`/talentshow/${showId}/live`)).data,
    refetchInterval: (q) => (q?.state?.data?.status === "live" ? 5000 : 8000),
  });
  // Lobby detail (entrants/visitors/fee) — only meaningful before it starts
  const { data: show } = useQuery({
    queryKey: ["ts-show", showId, user?.id],
    queryFn: async () => (await api.get(`/talentshow/${showId}`)).data,
    refetchInterval: 8000,
    enabled: (live?.status || "scheduled") !== "finished",
  });

  useEffect(() => {
    if (!showId || !user || !["entrant", "visitor", "spectator"].includes(live?.my_role || show?.my_role) || live?.status === "finished") return;
    const es = new EventSource(`${API}/talentshow/${showId}/stream`, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = () => {
      qc.invalidateQueries({ queryKey: ["ts-live", showId] });
      qc.invalidateQueries({ queryKey: ["ts-show", showId] });
    };
    return () => es.close();
  }, [showId, qc, user, live?.my_role, live?.status, show?.my_role]);
  const { data: inv } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data, enabled: Boolean(user) });
  const freeEntryQty = (inv?.tokens || []).find((t) => t.type === "talentshow_free_entry")?.qty || 0;

  const status = live?.status || show?.status || "scheduled";
  const myRole = live?.my_role || show?.my_role || null;
  const needsSpectatorEntry = status === "live" && (!myRole || myRole === "onlooker" || live?.access_required);
  const countdown = useCountdown(show?.scheduled_at || live?.scheduled_at);
  // Smooth local per-second countdown for the on-stage timer — the server
  // poll is now a slow SSE-backed safety net, so we tick this down locally
  // from `stage_ends_at` instead of only updating on each poll.
  const stageCountdown = useCountdown(live?.stage_ends_at);
  const stageTimeLeft = live?.stage_ends_at ? stageCountdown.ms / 1000 : (live?.time_left || 0);
  const [joinPassword, setJoinPassword] = useState("");
  const needsPassword = (show?.protected || live?.protected) && (!myRole || myRole === "onlooker") && !(show?.is_host || live?.is_host);

  // ── crowd reactions (like/dislike/praise/shame) + card praise/shame fx ──
  const [fx, setFx] = useState(null);
  const [reactions, setReactions] = useState([]);
  const [showoffs, setShowoffs] = useState([]);
  const processed = useRef(null);          // null until first live payload
  const fxQueue = useRef([]);
  const fxPlaying = useRef(false);
  const pumpFx = useLatestCallback(() => {
    if (fxPlaying.current) return;
    const next = fxQueue.current.shift();
    if (!next) return;
    fxPlaying.current = true;
    setFx({ kind: next.kind, intensity: next.intensity || 1, id: next.seq });
    setTimeout(() => { setFx(null); fxPlaying.current = false; pumpFx(); }, 1000);
  });
  // Clear lingering reactions whenever a new card takes the stage.
  useEffect(() => { setReactions([]); setShowoffs([]); }, [live?.current?.card_id]);
  useEffect(() => {
    if (live?.status !== "live") return;
    if (processed.current === null) { processed.current = live.event_seq || 0; return; } // skip backlog
    if ((live.event_seq || 0) <= processed.current) return;
    const curId = live?.current?.card_id;
    const since = processed.current;
    processed.current = live.event_seq;
    const relevant = (live.events || []).filter(
      (e) => e.seq > since && (!curId || e.card_id === curId)
    );
    // Crowd reactions (like/dislike/praise/shame)
    const rx = relevant.filter((e) => ["like", "dislike", "praise", "shame"].includes(e.kind));
    if (rx.length) {
      setReactions((prev) => [
        ...prev,
        ...rx.map((e) => ({ id: e.seq, kind: e.kind, from: e.from || "Someone", intensity: e.intensity || 1 })),
      ]);
      rx.forEach((e) => setTimeout(() => setReactions((p) => p.filter((r) => r.id !== e.seq)), 1700));
      const cardFx = rx.filter((e) => e.kind === "praise" || e.kind === "shame");
      if (cardFx.length) { fxQueue.current.push(...cardFx); pumpFx(); }
    }
    // Show-off flaunts → chat bubble over the stage card (Talent Show only)
    const so = relevant.filter((e) => e.kind === "showoff");
    if (so.length) {
      setShowoffs((prev) => [
        ...prev,
        ...so.map((e) => ({ id: e.seq, attr: e.attr, display: e.display, from: e.from })),
      ]);
      so.forEach((e) => setTimeout(() => setShowoffs((p) => p.filter((r) => r.id !== e.seq)), 3200));
    }
  }, [live, pumpFx]);

  const act = (fn) => async (...args) => {
    try { const r = await fn(...args); refresh?.(); ["ts-show", "ts-live"].forEach(key => qc.invalidateQueries({ queryKey: [key, showId] })); qc.invalidateQueries({ queryKey: ["ts-schedule"] }); qc.invalidateQueries({ queryKey: ["ts-my-cards"] }); return r; }
    catch (e) { toast.error(apiError(e, "Action failed")); }
  };

  const doEnter = act(async (ids, useToken) => {
    const r = await api.post(`/talentshow/${showId}/enter`, { card_ids: ids, use_token: !!useToken, password: joinPassword });
    setEnterOpen(false); qc.invalidateQueries({ queryKey: ["inventory"] });
    toast.success(r.data.used_token ? `Entered ${ids.length} card(s) · token used` : `Entered ${ids.length} card(s) · ${r.data.fee_paid}c`);
  });
  const doVisit = act(async (ids) => {
    await api.post(`/talentshow/${showId}/visit`, { card_id: ids?.[0] || null, password: joinPassword });
    setVisitOpen(false); toast.success("You're in the stands!");
  });
  const doSpectate = act(async (ids) => {
    await api.post(`/talentshow/${showId}/spectate`, { card_id: ids[0], password: joinPassword || null });
    setSpectateOpen(false);
    toast.success("You're spectating. Your card returns when you leave or the show ends.");
  });
  const doLeave = act(async () => { const r = await api.post(`/talentshow/${showId}/leave`); toast.success(r.data.refunded ? `Left · ${r.data.refunded}c refunded` : "Left the show."); });
  const doVote = act(async (kind) => { await api.post(`/talentshow/${showId}/vote`, { kind }); });
  const doShowoff = act(async (attr) => {
    const r = await api.post(`/talentshow/${showId}/showoff`, { attr });
    toast.success(`Flaunting ${r.data.display}!`);
  });
  const doHype = act(async () => {
    const r = await api.post(`/talentshow/${showId}/hype`);
    toast.success(`🔥 +${r.data.added_secs}s on the clock!`);
  });
  const doGift = act(async () => {
    await api.post(`/talentshow/${showId}/gift`, { kind: giftKind, amount: giftAmount });
    setGiftKind(null); toast.success(giftKind === "praise" ? "🌟 Praise sent!" : "🍅 Shame thrown!");
  });

  const feePreview = (n) => {
    const start = show?.my_entries?.length || 0;
    const step = show?.entry_fee_step ?? 5;
    let total = 0; for (let i = 1; i <= n; i++) total += step * (start + i);
    return total;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate("/talentshow")} data-testid="ts-back" className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-4">
        <ArrowLeft className="h-4 w-4" /> All shows
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-12 w-12 rounded-xl bg-orange-500/15 border border-orange-400/30 grid place-items-center"><Drama className="h-6 w-6 text-orange-300" /></div>
          <div className="min-w-0">
            <div className="font-display text-2xl text-white tracking-wide flex flex-wrap items-center gap-2">
              {(show?.custom || live?.custom) ? (
                <span className="flex items-center gap-1.5 min-w-0 [overflow-wrap:anywhere]" data-testid="ts-room-name"><Crown className="h-5 w-5 shrink-0 text-amber-300" /> {show?.name || live?.name}</span>
              ) : (
                <>{show ? fmtTime(show.scheduled_at) : "Talent Show"} Show</>
              )}
              {status === "live" && <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-rose-300"><Radio className="h-3 w-3 animate-pulse" /> Live</span>}
            </div>
            <div className="text-white/45 text-xs flex flex-wrap items-center gap-3 mt-0.5">
              {(show?.custom || live?.custom) && (show?.host_name || live?.host_name) && <span className="text-amber-200/70">hosted by {show?.host_name || live?.host_name}</span>}
              <span className="flex items-center gap-1"><Drama className="h-3 w-3" /> {live?.total || show?.entrant_count || 0} acts</span>
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {(live?.stands?.length ?? show?.visitor_count) || 0} watching</span>
            </div>
          </div>
        </div>
        {myRole && (status === "scheduled" || myRole === "spectator") && (
          <Button variant="outline" onClick={doLeave} data-testid="ts-leave" className="border-white/15 text-white/80"><LogOut className="h-4 w-4 mr-1" /> Leave</Button>
        )}
      </div>

      {/* ── LOBBY ── */}
      {status === "scheduled" && (
        <div className="space-y-6" data-testid="ts-lobby">
          {/* countdown to curtain */}
          <div className="hud-pill rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border border-orange-400/25 bg-gradient-to-r from-orange-500/10 to-fuchsia-500/10" data-testid="ts-countdown">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-orange-500/15 border border-orange-400/30 grid place-items-center"><Clock className="h-5 w-5 text-orange-300 animate-pulse" /></div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{countdown.started ? "Curtain rising" : "Curtain rises in"}</div>
                <div className="text-white/55 text-xs mt-0.5">{show ? `Show starts at ${fmtTime(show.scheduled_at)}` : ""}</div>
              </div>
            </div>
            <div className="font-display text-4xl sm:text-5xl tabular-nums tracking-wider" style={{ color: countdown.started ? "#34d399" : "#fbbf24", textShadow: `0 0 24px ${countdown.started ? "#34d399" : "#fbbf24"}66` }} data-testid="ts-countdown-clock">
              {countdown.started ? "STARTING…" : countdown.label}
            </div>
          </div>
          {(show?.custom && show?.description) && (
            <div className="hud-pill rounded-2xl p-4 text-sm text-white/70 border-l-2 border-amber-400/40" data-testid="ts-room-desc">{show.description}</div>
          )}
          {needsPassword && (
            <div className="hud-pill rounded-2xl p-4 flex items-center gap-3 border border-amber-400/30" data-testid="ts-join-password-box">
              <Lock className="h-4 w-4 text-amber-300 shrink-0" />
              <Input data-testid="ts-join-password" value={joinPassword} onChange={(e) => setJoinPassword(e.target.value)} placeholder="This show is locked — enter the password to join" className="bg-black/40 border-white/10 h-9 text-sm" />
            </div>
          )}
          {!myRole ? (
            <div className="hud-pill rounded-2xl p-6 flex flex-col sm:flex-row gap-3">
              <Button data-testid="ts-enter-btn" disabled={!user || (needsPassword && !joinPassword.trim())} onClick={() => setEnterOpen(true)} className="bg-fuchsia-500 text-white hover:bg-fuchsia-400 flex-1"><Drama className="h-4 w-4 mr-2" /> Enter cards to perform</Button>
              <Button data-testid="ts-visit-btn" disabled={!user || (needsPassword && !joinPassword.trim())} onClick={() => setVisitOpen(true)} className="bg-cyan-500 text-black hover:bg-cyan-400 flex-1"><Eye className="h-4 w-4 mr-2" /> Join with a card (judge)</Button>
            </div>
          ) : (
            <div className="hud-pill rounded-2xl p-4 flex items-center justify-between">
              <div className="text-sm text-white/70">You joined as <span className="text-orange-300 font-semibold">{myRole}</span>{myRole === "entrant" ? ` with ${show?.my_entries?.length || 0} card(s)` : ""}.</div>
              {myRole === "entrant" && <Button size="sm" data-testid="ts-enter-more" onClick={() => setEnterOpen(true)} className="bg-fuchsia-500 text-white hover:bg-fuchsia-400">+ Add card ({show?.next_card_fee}c)</Button>}
            </div>
          )}

          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 mb-3">Line-up · {show?.entrants?.length || 0}</div>
            <div className="flex flex-wrap gap-3">
              {(show?.entrants || []).map((e) => (
                <div key={e.card_id} className="flex flex-col items-center gap-1.5" data-testid="ts-lineup-card"><CardFace card={e} size="seat" /><span className="text-[10px] text-white/65 max-w-[7rem] truncate text-center font-medium">{e.owner_name}</span></div>
              ))}
              {(show?.entrants || []).length === 0 && <div className="text-white/30 text-sm">No acts yet — be the first to perform.</div>}
            </div>
          </div>
          <Stands stands={(show?.visitors || []).filter((v) => v.card).map((v) => ({ user_name: v.user_name, card: v.card }))} />
        </div>
      )}

      {/* ── LIVE COLOSSEUM ── */}
      {needsSpectatorEntry && <section data-testid="ts-spectator-entry" className="border-y border-violet-300/25 py-8 mb-6">
        <h2 data-testid="ts-spectator-entry-title" className="font-display text-lg text-violet-200 mb-2">SPECTATE THE ONGOING SHOW</h2>
        <p data-testid="ts-spectator-entry-rules" className="text-sm text-white/60 mb-4">Enter with one available card. Free, read-only entry. Your card is reserved while watching and released when you leave or the show ends; no forfeiture or voting rewards.</p>
        {needsPassword && <Input data-testid="ts-spectator-password" type="password" maxLength={40} value={joinPassword} onChange={e => setJoinPassword(e.target.value)} placeholder="Show password" className="mb-3 max-w-sm bg-black/30 border-white/15" />}
        <Button data-testid="ts-spectate-button" onClick={() => user ? setSpectateOpen(true) : navigate("/login")} disabled={Boolean(user && needsPassword && !joinPassword.trim())} className="bg-violet-600 text-white hover:bg-violet-500"><Eye className="h-4 w-4 mr-2" />{user ? "Choose a card to spectate" : "Sign in to spectate"}</Button>
      </section>}
      {status === "live" && !needsSpectatorEntry && (
        <div className="space-y-4" data-testid="ts-live">
          <Stands stands={live?.stands} />
          <div className="hud-pill rounded-2xl p-6 relative overflow-hidden">
            {/* progress */}
            <div className="flex items-center justify-between mb-4 text-xs text-white/50">
              <span>Act {Math.min((live?.index ?? 0) + 1, live?.total || 1)} / {live?.total || 0}</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {Math.ceil(stageTimeLeft)}s · {live?.voted_count}/{live?.eligible_voters} voted</span>
            </div>
            <div className="h-1 rounded-full bg-white/10 mb-6 overflow-hidden">
              <motion.div className="h-full bg-orange-400" animate={{ width: `${100 * (1 - stageTimeLeft / (live?.showcase_seconds || 10))}%` }} transition={{ ease: "linear", duration: 0.25 }} />
            </div>
            <AnimatePresence mode="wait"><ShowcaseStage current={live?.current} fx={fx} reactions={reactions} showoffs={showoffs} shownOff={live?.shown_off || []} /></AnimatePresence>

            {/* show-off + crowd hype */}
            <CrowdHype live={live} isOwner={live?.is_current_owner} myRole={myRole} onShowoff={doShowoff} onHype={doHype} />

            {/* controls */}
            {myRole === "visitor" ? (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
                <Button data-testid="ts-vote-like" onClick={() => doVote("like")} className={`${live?.my_vote === "like" ? "bg-emerald-400 text-black" : "bg-white/10 text-white"} hover:bg-emerald-400 hover:text-black`}><ThumbsUp className="h-4 w-4 mr-1" /> Like</Button>
                <Button data-testid="ts-vote-dislike" onClick={() => doVote("dislike")} className={`${live?.my_vote === "dislike" ? "bg-rose-400 text-black" : "bg-white/10 text-white"} hover:bg-rose-400 hover:text-black`}><ThumbsDown className="h-4 w-4 mr-1" /> Dislike</Button>
                <Button data-testid="ts-praise" onClick={() => { setGiftKind("praise"); setGiftAmount(PRAISE_AMOUNTS[0]); }} className="bg-amber-400 text-black hover:bg-amber-300"><Sparkles className="h-4 w-4 mr-1" /> Praise</Button>
                <Button data-testid="ts-shame" onClick={() => { setGiftKind("shame"); setGiftAmount(SHAME_AMOUNTS[0]); }} className="bg-purple-700 text-white hover:bg-purple-600"><Citrus className="h-4 w-4 mr-1" /> Shame</Button>
              </div>
            ) : (
              <div className="text-center text-white/40 text-xs mt-6">
                {myRole === "entrant" ? "You're performing — sit back and enjoy the show." : "Spectator · Read-only"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RESULTS ── */}
      {status === "finished" && <ResultsView results={live?.results || show?.results} />}

      {/* dialogs */}
      <CardPicker open={enterOpen} onClose={() => setEnterOpen(false)} multi title="Enter cards to perform"
        confirmLabel="Enter (locks cards)" feePreview={feePreview} onConfirm={doEnter}
        tokenOption={{ qty: freeEntryQty, name: "Talent Show Free Entry" }} />
      <CardPicker open={visitOpen} onClose={() => setVisitOpen(false)} multi={false} title="Pick your stand card"
        confirmLabel="Take a seat" onConfirm={doVisit} />
      <CardPicker open={spectateOpen && status === "live"} onClose={() => setSpectateOpen(false)} multi={false} title="Choose your spectator card" confirmLabel="Spectate with this card" onConfirm={doSpectate} />

      {/* gift amount dialog */}
      <Dialog open={!!giftKind} onOpenChange={() => setGiftKind(null)}>
        <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl text-white tracking-wide">{giftKind === "praise" ? "🌟 Shower Praise" : "🍅 Throw Shame"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Choose how many credits to spend praising or shaming the card on stage.</DialogDescription>
          <p className="text-white/50 text-xs">
            {giftKind === "praise" ? "Credits go straight to the card's owner. The more you spend, the brighter it shines and spins." : "Every 1C of shame locks the card for 1 second after the show — and darkens it more the harder you hit."}
          </p>
          <div className="flex gap-2 my-3">
            {(giftKind === "praise" ? PRAISE_AMOUNTS : SHAME_AMOUNTS).map((a) => (
              <button key={a} data-testid={`ts-gift-amt-${a}`} onClick={() => setGiftAmount(a)} className={`flex-1 rounded-lg py-2 border text-sm font-mono ${giftAmount === a ? "border-orange-400 bg-orange-400/20 text-white" : "border-white/15 text-white/60"}`}>{a}c</button>
            ))}
          </div>
          <Input data-testid="ts-gift-custom" type="number" min={1} value={giftAmount} onChange={(e) => setGiftAmount(Math.max(1, parseInt(e.target.value || "1")))} className="bg-black/40 border-white/10 mb-3" />
          <Button data-testid="ts-gift-confirm" onClick={doGift} className={`w-full ${giftKind === "praise" ? "bg-amber-400 text-black hover:bg-amber-300" : "bg-purple-700 text-white hover:bg-purple-600"}`}>
            {giftKind === "praise" ? `Praise with ${giftAmount}c` : `Shame for ${giftAmount}s`}
          </Button>
        </DialogContent>
      </Dialog>

      <ShowChat showId={showId} canWrite={myRole === "visitor"} myRole={needsSpectatorEntry ? null : myRole} />
    </div>
  );
}
